import React, {Component} from 'react';
import {ModalBody, ModalFooter} from 'reactstrap';
import {Spinner} from "../../Spinner";
import {ShopifyLinkComponent} from "../../agency/link_acocunts/ShopifyLink";
import { withTranslation } from 'react-i18next';
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from '../../../settings/local_settings';


class AddStoreFormClass extends Component {

    constructor(props) {
        super(props);

        this.state = {
            isSaving: false,
            storeSubdomain: null,
            detectedSubdomain: null,
            isDetecting: false,
            detectionError: null,
            inputValue: null
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleStoreSubdomainChange = this.handleStoreSubdomainChange.bind(this);
        this.handleStoreSubdomainBlur = this.handleStoreSubdomainBlur.bind(this);
        this.footerButton = this.footerButton.bind(this);
        this.processInput = this.processInput.bind(this);
        this.detectSubdomainFromPublicUrl = this.detectSubdomainFromPublicUrl.bind(this);
        this.extractSubdomainFromInput = this.extractSubdomainFromInput.bind(this);
    }

    extractSubdomainFromInput(input) {
        if (!input || !input.trim()) {
            return null;
        }

        const trimmed = input.trim();

        // Caso 1: URL completa myshopify.com (ej: xyz.myshopify.com o https://xyz.myshopify.com)
        const myshopifyMatch = trimmed.match(/(?:https?:\/\/)?([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
        if (myshopifyMatch) {
            return myshopifyMatch[1];
        }

        // Caso 2: Solo subdominio (ej: xyz)
        // Validar que sea un subdominio válido (solo letras, números y guiones, empezando con letra o número)
        const subdomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*$/;
        if (subdomainRegex.test(trimmed) && !trimmed.includes('.')) {
            return trimmed;
        }

        // Caso 3: URL pública (ej: www.zapatillas.com.ar)
        // Retornar null para indicar que necesita detección automática
        return null;
    }

    async detectSubdomainFromPublicUrl(publicUrl) {
        // Asegurar que la URL tenga protocolo
        let urlToFetch = publicUrl.trim();
        if (!urlToFetch.startsWith('http://') && !urlToFetch.startsWith('https://')) {
            urlToFetch = 'https://' + urlToFetch;
        }

        // Primero intentar usar endpoint backend (si existe)
        try {
            const backendResponse = await new APIClient(API_URL + 'shopify/detect-subdomain/').call('POST', {
                url: urlToFetch
            });

            if (backendResponse && backendResponse.status === 'OK' && backendResponse.data && backendResponse.data.subdomain) {
                return backendResponse.data.subdomain;
            }
        } catch (backendError) {
            // Si el endpoint no existe o falla, continuar con el método directo
            console.log('Backend endpoint no disponible, intentando detección directa...');
        }

        // Método directo desde el navegador (puede fallar por CORS)
        try {
            let response;
            let html;

            try {
                response = await fetch(urlToFetch, {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        'Accept': 'text/html'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                html = await response.text();
            } catch (fetchError) {
                // Detectar si es error de CORS
                const isCorsError = fetchError.name === 'TypeError' || 
                                   fetchError.message.includes('CORS') ||
                                   fetchError.message.includes('Failed to fetch') ||
                                   fetchError.message.includes('NetworkError');

                if (isCorsError) {
                    throw new Error('No se puede acceder a la URL debido a restricciones de seguridad del navegador (CORS). Por favor, ingresa el subdominio directamente (ej: xyz.myshopify.com o solo xyz).');
                }
                throw new Error(`Error al acceder a la URL: ${fetchError.message}`);
            }

            // Buscar Shopify.shop en el HTML
            // Puede aparecer como: Shopify.shop = "tienda.myshopify.com" o var Shopify = { shop: "tienda.myshopify.com" }
            const shopifyShopPatterns = [
                /Shopify\.shop\s*=\s*["']([^"']+\.myshopify\.com)["']/,
                /Shopify\s*=\s*\{[^}]*shop\s*:\s*["']([^"']+\.myshopify\.com)["']/,
                /"shop"\s*:\s*["']([^"']+\.myshopify\.com)["']/,
                /window\.Shopify\s*=\s*\{[^}]*shop\s*:\s*["']([^"']+\.myshopify\.com)["']/,
                /Shopify\.shop\s*=\s*["']([^"']+)["']/,
                /shop\s*:\s*["']([^"']+\.myshopify\.com)["']/
            ];

            for (const pattern of shopifyShopPatterns) {
                const match = html.match(pattern);
                if (match && match[1]) {
                    const fullDomain = match[1];
                    const subdomainMatch = fullDomain.match(/([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
                    if (subdomainMatch) {
                        return subdomainMatch[1];
                    }
                }
            }

            throw new Error('No se encontró información de Shopify en la página. Asegúrate de que sea una tienda de Shopify.');
        } catch (error) {
            if (error.message) {
                throw error;
            }
            throw new Error('Error al detectar el subdominio. Por favor, ingresa el subdominio directamente.');
        }
    }

    async processInput(input) {
        if (!input || !input.trim()) {
            this.setState({
                detectedSubdomain: null,
                isDetecting: false,
                detectionError: null
            });
            return;
        }

        // Primero intentar extraer directamente
        const extractedSubdomain = this.extractSubdomainFromInput(input);

        if (extractedSubdomain) {
            // Caso 1 o 2: Ya tenemos el subdominio
            this.setState({
                detectedSubdomain: extractedSubdomain,
                isDetecting: false,
                detectionError: null
            });
            return;
        }

        // Caso 3: Necesitamos detectar desde URL pública
        this.setState({
            isDetecting: true,
            detectionError: null
        });

        try {
            const detectedSubdomain = await this.detectSubdomainFromPublicUrl(input);
            this.setState({
                detectedSubdomain: detectedSubdomain,
                isDetecting: false,
                detectionError: null
            });
        } catch (error) {
            this.setState({
                detectedSubdomain: null,
                isDetecting: false,
                detectionError: error.message || 'Error al detectar el subdominio'
            });
        }
    }

    handleStoreSubdomainChange(event) {
        const value = event.target.value;
        this.setState({
            inputValue: value,
            storeSubdomain: value,
            detectedSubdomain: null,
            detectionError: null
        });
    }

    handleStoreSubdomainBlur(event) {
        const value = event.target.value;
        if (value && value.trim()) {
            this.processInput(value);
        }
    }

    handleSubmit(event) {
        event.preventDefault();

        this.setState({
            isSaving: true
        });
    }

    subdomainIsValid(subdomain) {
        if (!subdomain) {
            return false;
        }

        // Validate the subdomain by constructing the full URL and testing against the user's regex.
        const urlToValidate = `https://${subdomain}.myshopify.com`;
        // User's regex: /^https?\\:\\/\\/[a-zA-Z0-9][a-zA-Z0-9\\-]*\\.myshopify\\.com\\/?/
        // In JS literal, this is:
        const shopifyRegex = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com\/?/;

        return shopifyRegex.test(urlToValidate)
    }

    footerButton() {
        if (this.state.isSaving) {
            return <Spinner color="positive small"/>;
        }

        const subdomainToUse = this.state.detectedSubdomain || this.state.storeSubdomain;
        const trimmedSubdomain = subdomainToUse ? subdomainToUse.trim() : "";
        const subdomainIsValid = this.subdomainIsValid(trimmedSubdomain);

        return <ShopifyLinkComponent hasConnection={false} subdomain={trimmedSubdomain} disabled={!subdomainIsValid || this.state.isDetecting} />;
    }

    render() {
        const { t } = this.props;
        const subdomainToUse = this.state.detectedSubdomain || this.state.storeSubdomain;
        const showDetectedSubdomain = this.state.detectedSubdomain && this.state.detectedSubdomain !== this.state.storeSubdomain;

        return (
            <form onSubmit={this.handleSubmit}>
                <ModalBody>
                    <div className="modal-section">
                        <div className="form-row">
                            <div className="form-col col-12">
                                <label>{t('add_store_modal.shopify_subdomain_label', 'URL o Subdominio de Shopify')}</label>
                                <input name="storeSubdomain" id="storeSubdomain" type="text"
                                       value={this.state.storeSubdomain || ''}
                                       onChange={this.handleStoreSubdomainChange}
                                       onBlur={this.handleStoreSubdomainBlur}
                                       readOnly={false}
                                       className="form-control"
                                       required="required"
                                       placeholder={t('add_store_modal.shopify_input_placeholder', 'xyz, xyz.myshopify.com o www.tutienda.com')}
                                />
                                {this.state.isDetecting && (
                                    <div className="field-footnote mt-2 text-info">
                                        <Spinner color="positive small" /> {t('add_store_modal.detecting_subdomain', 'Detectando subdominio...')}
                                    </div>
                                )}
                                {this.state.detectionError && (
                                    <div className="field-footnote mt-2 text-danger">
                                        {t('add_store_modal.detection_error', 'Error:')} {this.state.detectionError}
                                    </div>
                                )}
                                {showDetectedSubdomain && !this.state.isDetecting && (
                                    <div className="field-footnote mt-2 text-success">
                                        {t('add_store_modal.subdomain_detected', 'Subdominio detectado:')} <strong>{this.state.detectedSubdomain}</strong>
                                    </div>
                                )}
                                <div className="field-footnote mt-2">
                                    {t('add_store_modal.shopify_input_help', 'Puedes ingresar el subdominio (ej: xyz), la URL completa (ej: xyz.myshopify.com) o la URL pública de tu tienda (ej: www.tutienda.com).')}
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {this.footerButton()}
                </ModalFooter>
            </form>
        )
    }
}

export const AddStoreForm = withTranslation()(AddStoreFormClass);
